/**
 * Renders admin-defined custom fields as form inputs (PLAN.md §5.21).
 *
 * Nothing here is hard-coded per field: the definitions come from the API, so a field created in
 * admin appears on the ticket form immediately, with no frontend deploy. That is the Phase 1
 * exit criterion.
 *
 * **The client does not enforce validation — the server does.** These inputs pick the right
 * control and apply the obvious HTML constraints so the form is pleasant to use, but the backend
 * validates every custom-field write regardless (`services/custom_fields.validate_values`), and
 * its rejection is what the user sees if the two ever disagree. Client-side checks here are a
 * convenience, never a gate.
 */

import type { CustomField } from '@/types/servicedesk';
import { Select } from '@/components/common/Select';

export function CustomFieldInputs({
  definitions,
  values,
  onChange,
  /** Limits which fields apply, matching the backend's queue/type scoping. */
  queueId,
  typeId,
  disabled,
}: {
  definitions: CustomField[];
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  queueId?: string | null;
  typeId?: string | null;
  disabled?: boolean;
}) {
  const applicable = definitions.filter((definition) => {
    if (!definition.is_active) return false;
    // An empty scope list means "every queue" / "every type", matching the backend.
    if (definition.queue_ids.length > 0 && queueId && !definition.queue_ids.includes(queueId)) {
      return false;
    }
    if (
      definition.ticket_type_ids.length > 0 &&
      typeId &&
      !definition.ticket_type_ids.includes(typeId)
    ) {
      return false;
    }
    return true;
  });

  if (applicable.length === 0) return null;

  const set = (key: string, value: unknown) => onChange({ ...values, [key]: value });

  return (
    <div className="space-y-4">
      {applicable.map((definition) => (
        <label key={definition.id} className="block">
          <span className="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-200">
            {definition.label}
            {definition.is_required && <span className="ml-0.5 text-red-500">*</span>}
          </span>
          <FieldControl
            definition={definition}
            value={values[definition.key]}
            onChange={(value) => set(definition.key, value)}
            disabled={disabled}
          />
          {definition.help_text && (
            <span className="mt-1 block text-xs text-surface-500">{definition.help_text}</span>
          )}
        </label>
      ))}
    </div>
  );
}

function FieldControl({
  definition,
  value,
  onChange,
  disabled,
}: {
  definition: CustomField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}) {
  const common = { disabled, className: 'input-base', 'aria-label': definition.label };

  switch (definition.field_type) {
    case 'long_text':
      return (
        <textarea
          {...common}
          rows={3}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'number':
    case 'decimal':
      return (
        <input
          {...common}
          type="number"
          value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''}
          // Empty must become null, not 0 — "not filled in" and "zero" are different answers.
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );

    case 'checkbox':
      return (
        <input
          type="checkbox"
          disabled={disabled}
          aria-label={definition.label}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="rounded border-surface-300 text-primary-500 focus:ring-primary-500"
        />
      );

    case 'date':
    case 'datetime':
      return (
        <input
          {...common}
          type={definition.field_type === 'date' ? 'date' : 'datetime-local'}
          value={typeof value === 'string' ? value.slice(0, definition.field_type === 'date' ? 10 : 16) : ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );

    case 'dropdown':
      return (
        <Select
          ariaLabel={definition.label}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(next) => onChange(next || null)}
          options={[
            { value: '', label: '—' },
            ...definition.options.map((option) => ({
              value: option.value,
              label: option.label ?? option.value,
            })),
          ]}
        />
      );

    case 'multi_select': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-3 rounded-lg border border-surface-300 p-2 dark:border-surface-600">
          {definition.options.map((option) => (
            <label key={option.value} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.includes(option.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, option.value]
                      : selected.filter((item) => item !== option.value),
                  )
                }
                className="rounded border-surface-300 text-primary-500 focus:ring-primary-500"
              />
              {option.label ?? option.value}
            </label>
          ))}
        </div>
      );
    }

    case 'url':
      return (
        <input
          {...common}
          type="url"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );

    default:
      return (
        <input
          {...common}
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
  }
}
